import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { InvoiceLineModel } from './invoiceline.model';
import { ConceptModel } from '../../concept/concept/concept.model';
import { InvoiceTaxModel } from './invoicetax.model';
import { ConceptService } from '../../concept/concept/concept.service';
import { map, Observable } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TaxCalculator } from '../../../shared/taxCalculator';
import { ConceptSerialNumberComponent } from '../../concept/concept/serialNumber/concept-serialNumber.component';
import { BarcodeDialogComponent } from '../preinvoice/barcode-dialog.component';

@Component({
    selector: 'convfact-deliverynote-form-item',
    templateUrl: './deliverynote-form-item.component.html',
    styleUrls: [],
    standalone: false
})

export class DeliveryNoteFormItemComponent implements OnInit {
  @Input() item = new InvoiceLineModel();
  @Input() taxes = Array<InvoiceTaxModel>();
  @Input() concepts = Array<ConceptModel>();
  chipsArray: [{}];

  conceptCtrl: UntypedFormControl;
  filteredConcepts: Observable<ConceptModel[]>;

  // Preview del código de barras del concepto seleccionado en esta línea —
  // solo informativo en el formulario, no se persiste en InvoiceLineModel.
  conceptBarcode: string | null = null;
  barcodeInvalid = false;
  @ViewChild('lineBarcodePreview') barcodePreviewEl: ElementRef<SVGElement>;

  constructor(private conceptService: ConceptService,
    public dialogRef: MatDialogRef<DeliveryNoteFormItemComponent>, @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private taxCalculator: TaxCalculator,
    public snackBar: MatSnackBar,) {
  }

  //Detectar código de barras en campo Concepto
  onConceptEnter(code: string) {
    console.log('Código escaneado o introducido:', code);
    this.conceptService.barcode(code).then(result => {
      if (result) {
        this.item.product = result.name;
        this.item.conceptUid = result.uid;
        this.item.quantity = 1;
        this.item.unityAmount = result.netPrice - (result.netPrice * (result.discount / 100));
        this.item.netPrice = result.netPrice;
        if (result.taxes && result.taxes.length > 0) {
          this.item.taxes = result.taxes;
        }
        this.item.discount = 0;
      } else {
        alert('No se encontró concepto para el código: ' + code);
      }
    });
  }
  //------------------------------------------

  toZero() {
    this.item.unityAmount = null;
  }

  filterConcepts(val) {
    const self = this;
    for (const concept of this.concepts) {
      if (concept.name == val) {
        self.item.conceptUid = concept.uid;
        self.item.quantity = 1;
        self.item.unityAmount = concept.netPrice - (concept.netPrice * (concept.discount / 100));
        self.item.netPrice = concept.netPrice;
        if (concept.taxes && concept.taxes.length > 0) {
          self.item.taxes = concept.taxes;
        }
        self.item.discount = 0;
      }
    }

    return val ? this.concepts.filter(s => s.shortcut.toLowerCase().indexOf(val.toLowerCase()) === 0)
      : this.concepts;
  }

  async renderLineBarcode() {
    this.barcodeInvalid = false;
    if (!this.conceptBarcode || !this.barcodePreviewEl?.nativeElement) return;
    try {
      const JsBarcode = (await import('jsbarcode')).default;
      JsBarcode(this.barcodePreviewEl.nativeElement, this.conceptBarcode, {
        format: 'CODE128',
        width: 2,
        height: 40,
        displayValue: true,
        fontSize: 12,
        margin: 6,
      });
    } catch (error) {
      console.error('Error al generar el código de barras de la línea:', error);
      if ((error as { name?: string })?.name === 'InvalidInputException') {
        this.barcodeInvalid = true;
      }
      // Cualquier otro error (chunk no cargado, etc.) simplemente no muestra
      // el preview — no es un dato crítico para poder guardar la línea.
    }
  }

  ngOnInit() {
    this.conceptService.getList().then(
      concepts => {
        this.concepts = concepts;

        // Edición de una línea ya existente: si el concepto tiene código de
        // barras, mostrarlo también aunque no se acabe de seleccionar ahora.
        const selected = concepts.find(c => c.uid === this.item.conceptUid);
        if (selected?.barcode) {
          this.conceptBarcode = selected.barcode;
          setTimeout(() => this.renderLineBarcode(), 0);
        }
      }
    )

    this.conceptCtrl = new UntypedFormControl();
    this.filteredConcepts = this.conceptCtrl.valueChanges
      .pipe(
        map(concept => concept && typeof concept === 'object' ? concept.shortcut : concept),
        map(name => this.filterConcepts(name))
      );

    this.taxes = this.data.taxes;

    const indexes = [];

    for (let i = 0; i < this.data.item.taxes.length; i++) {
      if (this.data.item.taxes[i] == null) {
        indexes.push(i);
      }
    }

    for (let j = 0; j < indexes.length; j++) {
      this.data.item.taxes.splice(j, 1);
    }

    // Añadimos la linea basica que nos llega, pero sin añadirle los impuestos
    this.item = {
      product: this.data.item.product,
      conceptUid: this.data.item.conceptUid,
      conceptCode: this.data.item.conceptCode,
      quantity: this.data.item.quantity,
      discount: this.data.item.discount,
      unityAmount: this.data.item.unityAmount,
      netPrice: this.data.item.netPrice,
      position: this.data.item.position,
      totalLine: this.data.item.totalLine,
      codigoTrazabilidad: this.data.item.codigoTrazabilidad,
      taxes: new Array<InvoiceTaxModel>(),
    };

    // Añadimos los impuestos que tenemos que añadir, usando el metodo correspondiente que incluye la logica necesaria
    for (let i = 0; i < this.data.item.taxes.length; i++) {
      this.setTax(this.data.item.taxes[i], this.item);
    }

  }

  onSelectTax(taxId: number): void {
    const taxFinded = this.taxes.find(c => c.id == taxId);

    if (taxFinded) {
      this.setTax(taxFinded, this.item)
    }

  }

  setTax(tax: InvoiceTaxModel, item: InvoiceLineModel): void {
    tax.taxId = tax.id;

    const alreadyApplied = item.taxes.some(t => t.id === tax.id);
    if (alreadyApplied) {
      this.displayInfo('Este impuesto ya ha sido aplicado.');
      return;
    }

    const isRecargoEquiv = tax.name.toLowerCase().includes('recargo equiv');
    const impuestosDelMismoTipo = item.taxes.filter(t => t.type === tax.type && !t.name.toLowerCase().includes('recargo equiv'));

    // Si ya hay un impuesto principal del mismo tipo, no permitir otro
    if (!isRecargoEquiv && impuestosDelMismoTipo.length > 0) {
      this.displayInfo(`Ya se ha aplicado un impuesto del tipo ${tax.type}.`);
      return;
    }

    item.taxes.push(tax);
  }

  displayFn(concept: ConceptModel) {
    if (concept != null) {
      this.item.conceptUid = concept.uid;
      this.item.quantity = 1;
      this.item.unityAmount = concept.netPrice - (concept.netPrice * (concept.discount / 100));
      this.item.netPrice = concept.netPrice;

      this.conceptBarcode = concept.barcode || null;
      if (this.conceptBarcode) {
        setTimeout(() => this.renderLineBarcode(), 0);
      }

      if (concept.discount != null && concept.discount > 0) {
        this.item.product = concept.name + " - " + concept.discount + "% descuento (aplicado a " + concept.netPrice + ")";
      }

      if (concept.barcode || concept.model || concept.size) {
        const extras: string[] = [];

        if (concept.barcode) {
          extras.push(`Código: ${concept.barcode}`);
        }
        if (concept.model) {
          extras.push(`Modelo: ${concept.model}`);
        }
        if (concept.size) {
          extras.push(`Talla: ${concept.size}`);
        }

        this.item.product += " - " + extras.join(" - ");
      }

      if (concept.serialNumber) {
        const dialogRef = this.dialog.open(ConceptSerialNumberComponent, {
          width: '640px',
          maxWidth: '96vw',
          height: '70%',
          data: {
            concept: concept,
            live: true
          }
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.item.product = this.item.product + " - S/N: " + result;
          }
        });
      }
    }
  }

  displayInfo(message: string): void {
    const options = new MatSnackBarConfig();
    options.duration = 3000;
    options.panelClass = ['custom-snackbar', 'custom-snackbar-container']; this.snackBar.open(message, '', options);
  }

  changeChips = function (element: DeliveryNoteFormItemComponent) {
    element.item.taxes.forEach(tax => {
      element.setTax(tax, element.item);
    });
  }


  calcularTotalesLinea(invoiceLine) {
    invoiceLine.totalLine = this.taxCalculator.calcularTotalLinea(invoiceLine);
    return invoiceLine.totalLine;
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  onSave(): void {
    this.dialogRef.close({ action: 'save', item: this.item });
  }

  barcode() {
    this.dialog.open(BarcodeDialogComponent, {
    })
      .afterClosed().subscribe(result => {
        const data = result['concept'];
        if (data.uid !== "00000000-0000-0000-0000-000000000000") {
          this.item.conceptCode = data.conceptCode;
          this.item.conceptUid = data.uid;
          this.item.netPrice = data.netPrice;
          this.item.product = data.name;
          if (data.taxes && data.taxes.length > 0) {
            this.item.taxes = data.taxes;
          }
          this.item.unityAmount = data.netPrice;
          if (data['serialNumber']) {
            const dialogRef = this.dialog.open(ConceptSerialNumberComponent, {
              width: '640px',
              maxWidth: '96vw',
              height: '70%',
              data: {
                concept: data,
                live: true
              }
            });
            dialogRef.afterClosed().subscribe(result => {
              if (result) {
                this.item.product = this.item.product + " - S/N: " + result;
              }
            });
          }
        }

      });
  }

  public trackByIndex(i: number) { return i; }

}
